import * as Enumerable from "linq";
import { SubtaskCorrespondenceEmailModel, CorrespondenceResponse, CorrespondenceClientModel, SubtaskStatusEnum, RfiTemplateCategoryEnum } from "../../../../../models/vetting/vetting.models";
import { SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { transient, autoinject, computedFrom, observable, useView } from "aurelia-framework";
import { CorrespondenceItemModel, CargoTypeCategoryEnum, CorrespondenceEntityModel, CompCorrespondenceModel, CorrespondenceAssignmentFeatureModel } from "../../../../../models/vessels/vessel.models";
import { UserSecurityInfoModel } from "../../../../../models/authentication/token.models";
import { SubtasksService } from "../../../../../services/subtasks/subtasks.service";
import { Router } from "aurelia-router";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue } from "../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { EntityAttachmentModel, RightshipEntityAttachmentModel } from "../../../../../models/attachment/attachment.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { MultiSelectLookupControl } from "../../../../components/lookups/multi-select-lookup-control/multi-select-lookup-control";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import KeyDownEvent = JQuery.KeyDownEvent;
import { RightshipEntityAttachmentManager } from "../rightship-entity-attachment/rightship-entity-attachment-manager";
import { Subscription, EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../../../configuration/event-messages.constants";
import { BindingSignaler } from "aurelia-templating-resources";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { RulesHolder, ValidationActivation, ValidationTypeEnum } from "../../../../../client-validation-system/validations.models";
import { ClientSideValidator, EntityValidationsService } from "../../../../../client-validation-system/validations-service";
import * as moment from "moment";
import { Guid } from "../../../../../utilities/guid";
import { AttachmentClientService } from "../../../../../services/attachment/attachments-client.service";
import { RightshipConfirmationDialogModel } from "../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { NotifyPropertyChangedBase, Reflection } from "../../../../../utilities/reflection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";

@autoinject
@transient()
@useView('views/pages/modules/common-steps/correspondence/correspondence.html')
export class Correspondence extends NotifyPropertyChangedBase {
    accept: string = "application/pdf,image/png,image/jpeg,image/jpg,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-outlook,.msg";

    isReplyShowing: boolean;
    hideReply: boolean;
    allItems: CorrespondenceItemModel[] = null;
    items: { value: { value: CorrespondenceItemModel[]; key: number }[]; key: string }[] = [];
    currentTask: CorrespondenceItemModel;
    correspondenceMessage: string;
    comment: string = "";
    _changeStatusToAwaitingResponse: boolean = false;
    statusSnippet: string = "";
    correspondence: CorrespondenceResponse;
    toEmails: SubtaskCorrespondenceEmailModel[] = [];
    isEmailFixed: boolean;
    title: string;
    rfiChanged: (() => void);
    newDocumentAdded: (() => void);
    subtaskStatuses: LookupValue[];
    currentUser: UserSecurityInfoModel;
    assignment: CorrespondenceAssignmentFeatureModel;
    rfiSignature: string;
    onSubtaskCorrespondenceReplyFunc: (x: CorrespondenceClientModel) => void;
    refreshSubtaskStatusFunc: (status) => void;
    public canProcessSanctionedVet: boolean;
    public canProcessVetUnknownPort: boolean;
    public canEditRFICorrespondence: boolean;
    public correspondenceEntity: CorrespondenceEntityModel
    public showAddRFI: boolean = true;
    public entityHasOutcome: boolean = false;
    public correspondenceCount?: number;
    public sendCloseOutEmail: boolean = false;
    public initialSubtaskLinkedEntityId: number;
    public initialSubtaskLinkedEntityTypeId: number;
    public initialSubtaskId: number;
    public showFilters: boolean = true;
    emails: SubtaskCorrespondenceEmailModel[];
    _dateFrom: Date;

    // 1	General
    // 2	PSC
    // 3	Incident
    // 4	Forms
    // 5	FRB
    // 6	GHG Verification
    // 7	CFR
    // 8	RightShip Inspection
    private canShowAwaitingResponseCheckBox: boolean;

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z.currentTask),
        Reflection.getPropertyPath<Correspondence>(z => z.currentTask.entityTypeId))
    get isVesselApplicationForm(): boolean {
        if (this.currentTask && this.currentTask.entityTypeId == EntityTypes.VesselApplicationForms)
            return true;
        return false;
    }

    @computedFrom("_changeStatusToAwaitingResponse")
    get changeStatusToAwaitingResponse() {
        return this._changeStatusToAwaitingResponse;
    }

    @computedFrom('entityTypeId',
        Reflection.getPropertyPath<Correspondence>(z => z.canShowAwaitingResponseCheckBox),
        Reflection.getPropertyPath<Correspondence>(z => z.currentTask),
        Reflection.getPropertyPath<Correspondence>(z => z.currentTask.entityTypeId)
    )
    get canShowAwaitingResponseCheckboxIfVet(): boolean {
        return !(!this.canShowAwaitingResponseCheckBox && this.currentTask?.entityTypeId == EntityTypes.Vet);
    }

    set changeStatusToAwaitingResponse(x: boolean) {
        this._changeStatusToAwaitingResponse = x;

        if (!this.isVesselApplicationForm)
            this.setRFIRules();
    }

    validator: ClientSideValidator;

    private setRFIRules() {
        if (this.validator && this.validator.dispose)
            this.validator.dispose();

        const rulesHolderEmails: RulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "toEmails",
            isCollection: true,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Email is required",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Email is not in correct format",
                parameters: null, property: "email",
                validationType: ValidationTypeEnum.Email
            }]
        };

        const rulesHolder: RulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "RFI",
            isCollection: false,
            nestedRulesHolder: [rulesHolderEmails],
            rules: [{
                errorMessage: "Message is required",
                parameters: null, property: "correspondenceMessage",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Status snippet max length is 200 characters",
                parameters: '{ "length": 200 }', property: "statusSnippet",
                validationType: ValidationTypeEnum.MaxLength
            }]
        };

        if (this._changeStatusToAwaitingResponse == true) {
            rulesHolder.rules.push({
                errorMessage: "Status snippet is required",
                parameters: null, property: "statusSnippet",
                validationType: ValidationTypeEnum.Required
            });
            rulesHolder.rules.push({
                errorMessage: "Comments are required",
                parameters: null, property: "comment",
                validationType: ValidationTypeEnum.Required
            });
        }
        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    map: { entityType: EntityTypes, rfiTemplateCategory: RfiTemplateCategoryEnum, dependencies?: EntityTypes[] }[] = [
        {
            entityType: EntityTypes.Vessel,
            rfiTemplateCategory: null,
            dependencies: [
                EntityTypes.GHGVerification, EntityTypes.RightShipInspection,
                EntityTypes.Cfr, EntityTypes.Incident, EntityTypes.PSC,
                EntityTypes.Vet, EntityTypes.SafetyRatingReview, EntityTypes.FBR,
                EntityTypes.SafetyRatingReview, EntityTypes.VesselApplicationForms
            ]
        },
        { entityType: EntityTypes.PSC, rfiTemplateCategory: RfiTemplateCategoryEnum.PSC },
        { entityType: EntityTypes.Incident, rfiTemplateCategory: RfiTemplateCategoryEnum.Incident },
        {
            entityType: EntityTypes.Vet,
            rfiTemplateCategory: RfiTemplateCategoryEnum.VET,
            dependencies: [EntityTypes.Vet, EntityTypes.Incident, EntityTypes.PSC]
        },
        {
            entityType: EntityTypes.SafetyRatingReview, rfiTemplateCategory: RfiTemplateCategoryEnum.SafetyRatingReview
        },
        {
            entityType: EntityTypes.FBR,
            rfiTemplateCategory: RfiTemplateCategoryEnum.FBR,
            dependencies: [EntityTypes.FBR, EntityTypes.Cfr]
        },
        { entityType: EntityTypes.Cfr, rfiTemplateCategory: RfiTemplateCategoryEnum.CFR },
        { entityType: EntityTypes.RightShipInspection, rfiTemplateCategory: RfiTemplateCategoryEnum.RightShipInspection },
        { entityType: EntityTypes.GHGVerification, rfiTemplateCategory: RfiTemplateCategoryEnum.GHG },
        { entityType: EntityTypes.VoyageDataSubmission, rfiTemplateCategory: RfiTemplateCategoryEnum.VoyageDataSubmission },
        { entityType: EntityTypes.VesselApplicationForms, rfiTemplateCategory: null },

    ];

    name: string;

    applySubtaskStatusFilters(x: any) {
        this.selectedSubtaskStatusIds = x.filters.map(x => x.id);
        this.showSubtaskStatusFilter = false;
    }

    applyVetTasksFilters(x: any) {
        this.selectedVetTaskIds = x.filters.map(x => x.id);
        this.showVetTasksFilter = false;
    }

    applyRfiTypeFilters(x: any) {
        this.selectedRFITypeIds = x.filters.map(x => x.id);
        this.showRFITypeFilter = false;
    }

    applyEntityTypeFilters(x: any) {
        this.selectedEntityTypeIds = x.filters.map(x => x.id);
        this.showEntityTypeFilter = false;
    }

    canAssignToMe() {
        return !this.correspondenceEntity.isInconsistentState;
    }

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z.currentTask),
        Reflection.getPropertyPath<Correspondence>(z => z.currentTask.entityTypeId))
    get subtaskCategory() {
        if (this.currentTask) {
            const cat = this.map.filter(x => x.entityType == this.currentTask.entityTypeId)[0];
            if (cat)
                return cat.rfiTemplateCategory
        }
        return null;
    }

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z.currentTask),
        Reflection.getPropertyPath<Correspondence>(z => z.currentTask.entityTypeId))
    get isVet() {
        return this.currentTask.entityTypeId == EntityTypes.Vet;
    }

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z.currentTask),
        Reflection.getPropertyPath<Correspondence>(z => z.currentTask.entityTypeId))
    get isVoyageDataSubmission() {
        return this.currentTask.entityTypeId == EntityTypes.VoyageDataSubmission;
    }

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z.currentTask),
        Reflection.getPropertyPath<Correspondence>(z => z.currentTask.isInvitation))
    get isInvitation() {
        return this.currentTask.isInvitation;
    }

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z._dateFrom))
    get dateFrom() {
        return this._dateFrom;
    }

    set dateFrom(x: Date) {
        this._dateFrom = x;
        this.filterItems();
    }

    _dateTo: Date;

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z._dateTo))
    get dateTo() {
        return this._dateTo;
    }

    set dateTo(x: Date) {
        this._dateTo = x;
        this.filterItems();
    }

    _subscriptionToDocumentClickEvent: Subscription;
    canCreateInternalRfi: boolean = false;

    constructor(private notificationService: NotificationClientService,
        private i18N: I18N,
        private subtasksService: SubtasksService,
        private router: Router, private entityValidationsService: EntityValidationsService,
        public attachmentDialogBroker: RightshipEntityAttachmentManager,
        private signalClientService: SignalClientService,
        private lookupClientService: LookupClientService,
        private dialogService: DialogClientService,
        private bindingSignaler: BindingSignaler,
        private eventAggregator: EventAggregator,
        private attachmentClientService: AttachmentClientService,
        userClaimsService: UserClaimsService,
        userClientService: UserClientService) {
        super();
        this.attachmentDialogBroker.accept = this.accept;
        this.refreshSubtaskStatusFunc = this.refreshSubtaskStatus.bind(this);
        this.onSubtaskCorrespondenceReplyFunc = this.onSubtaskCorrespondenceReply.bind(this);

        this.canProcessSanctionedVet = userClaimsService.hasPermission(Claims.CanProcessSanctionedVet);
        this.canProcessVetUnknownPort = userClaimsService.hasPermission(Claims.CanProcessVetUnknownPort);
        this.canEditRFICorrespondence = userClaimsService.hasPermission(Claims.EditRFICorrespondence);
        this.canCreateInternalRfi = userClaimsService.hasPermission(Claims.CanCreateInternalRfi);
        this.canShowAwaitingResponseCheckBox = userClaimsService.hasPermission(Claims.ShowChangestatustoAwaitingcheckbox);
        this.currentUser = userClientService.currentUser;
        this.rfiSignature = '<br/><p>Whenever a response is required, please ensure that it is submitted using the below provided link. <br> Failure to use this link to provide a response may cause delays in processing.</p><p><br/><p>Kind regards,</p><p>RightShip Operations</p>';
    }

    public activate(model: CompCorrespondenceModel) {
        if (model && model.correspondenceEntity) {
            this.hideReply = model.hideReply;
            this.isReplyShowing = model.isReplyShowing;
            this.name = model.name;
            this.correspondenceEntity = model.correspondenceEntity;
            this.showAddRFI = model.showAddRFI == false ? false : true;
            this.entityHasOutcome = model.hasOutcome == true ? true : false;
            this.emails = model.emails;
            this.sendCloseOutEmail = model.sendCloseOutEmail == true;
            this.assignment = model.correspondenceEntity.assignment;
            this.initialSubtaskId = model.initialSubtaskId;
            this.initialSubtaskLinkedEntityId = model.initialSubtaskLinkedEntityId;
            this.initialSubtaskLinkedEntityTypeId = model.initialSubtaskLinkedEntityTypeId;
            this.showFilters = model.showFilters;
            this.rfiChanged = model.rfiChanged;
            this.newDocumentAdded = model.newDocumentAdded;
            this.initializeFilters();
            this.loadSubtasks();
            this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closeAllDropdowns.bind(this));
        }
    }

    closingRFI: boolean;

    async closeRFI() {
        if (this.closingRFI || this.entityHasOutcome) return;
        const isAssignedToMe = this.assignment && this.assignment.isAssignedToMe();
        if (!this.assignment || isAssignedToMe) {
            try {
                this.closingRFI = true;
                await this.subtasksService.closeRFI(this.currentTask.subtaskId, this.sendCloseOutEmail);
                if (this.rfiChanged) {
                    this.rfiChanged();
                }
                this.currentTask.subtaskStatusId = SubtaskStatusEnum.Closed;
                this.closingRFI = false;
            } catch {
                this.closingRFI = false;
            }
        } else {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: this.getDialogName(),
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                callback: () => this.closeRFI(),
                currentAssignment: this.assignment && this.assignment.assignedTo ?
                    this.assignment.assignedToUserName : ''
            }, true, false, false);
        }
    }

    loadSubtasks() {
        if (this.correspondenceEntity) {
            this.subtasksService.getCorrespondenceForEntity(this.correspondenceEntity.entityId, this.correspondenceEntity.entityTypeId, this.correspondenceFilter, this.selectedRFITypeIds).then(x => {
                this.allItems = x;

                this.correspondenceCount = x.length;

                this.bindingSignaler.signal('update-title');

                this.filterItems();

                if (this.initialSubtaskLinkedEntityId && this.initialSubtaskLinkedEntityTypeId) {
                    const subtaskToOpen = this.allItems.filter(a => a.subtaskLinkedEntities.filter(x => x.linkedEntityTypeId == this.initialSubtaskLinkedEntityTypeId).map(x => x.linkedEntityId).indexOf(this.initialSubtaskLinkedEntityId) != -1)[0];

                    if (subtaskToOpen) {
                        this.viewTask(subtaskToOpen);
                    }
                } else if (this.initialSubtaskId) {
                    const subtaskToOpen = this.allItems.filter(a => a.subtaskId == this.initialSubtaskId)[0];

                    if (subtaskToOpen) {
                        this.viewTask(subtaskToOpen);
                    }
                }
            });
        }
    }

    editSubtask() {
        this.addSubtask(this.currentTask.isInternal, this.currentTask.subtaskId);
    }

    addSubtask(isInternal: boolean, subtaskId: number = null) {
        const isAssignedToMe = this.assignment && this.assignment.isAssignedToMe();
        if (this.isVessel || isAssignedToMe) {
            const requestedBy = this.correspondenceEntity.requestedBy;
            const liaseWithShipManagers = this.correspondenceEntity.liaseWithShipManagers;

            this.dialogService.show(RightShipDialogServiceConstants.URLs.AddSubtaskDialog, {
                subtaskId: subtaskId,
                entityId: this.correspondenceEntity.entityId,
                isInternal: isInternal,
                requestedBy: requestedBy,
                liaseWithShipManagers: liaseWithShipManagers,
                inspectionTypeId: this.correspondenceEntity.inspectionTypeId,
                dataSources: this.correspondenceEntity.dataSources,
                entityTypeId: this.correspondenceEntity.entityTypeId,
                isWet: this.correspondenceEntity.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet,
                emails: this.emails,
                reload: (() => {
                    this.closeCorrespondence();
                    this.loadSubtasks();
                })
            },
                true, false, false, 'right');
        } else {
            if (this.canAssignToMe())
                this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                    name: this.getDialogName(),
                    submit: async () => {
                        if (this.assignment) {
                            await this.assignment.assignToMe();
                        }
                    },
                    callback: () => this.addSubtask(isInternal, subtaskId),
                    currentAssignment: this.assignment && this.assignment.assignedTo ? this.assignment.assignedToUserName : ''
                }, true, false, false);
        }
    }

    onSubtaskAddedFunc = () => {
        this.loadSubtasks();
    }

    attached() {
        this.signalClientService.subscribe(SignalFunctions.subtaskAdded, this.onSubtaskAddedFunc);
        this.signalClientService.subscribe(SignalFunctions.subtaskStatusChanged, this.refreshSubtaskStatusFunc);

        this.setRFIRules();
        this.subscribeSignalR();
    }

    detached() {
        this.unsubscribeSignalR();
        this.signalClientService.unsubscribe(SignalFunctions.subtaskStatusChanged, this.refreshSubtaskStatusFunc);
        this.signalClientService.unsubscribe(SignalFunctions.subtaskAdded, this.onSubtaskAddedFunc);
        if (this._subscriptionToDocumentClickEvent && this._subscriptionToDocumentClickEvent.dispose)
            this._subscriptionToDocumentClickEvent.dispose();
    }

    openTask() {
        this.router.navigate(`${this.currentTask.type.toLowerCase()}/${this.currentTask.id}`);
    }

    unsubscribeSignalR() {
        if (!this.currentTask) return;
        this.signalClientService.unsubscribeToGroup(SignalFunctions.onSubtaskCorrespondenceReply, `correspondence-${this.currentTask.subtaskGuid}`, this.onSubtaskCorrespondenceReplyFunc);
    }

    subscribeSignalR() {
        if (!this.currentTask) return;
        this.signalClientService.subscribeToGroup(SignalFunctions.onSubtaskCorrespondenceReply, `correspondence-${this.currentTask.subtaskGuid}`, this.onSubtaskCorrespondenceReplyFunc);
    }

    async viewTask(task: CorrespondenceItemModel) {
        this.unsubscribeSignalR();
        this.title = task.type;
        this.currentTask = task;
        this.correspondence = await this.subtasksService.getCorrespondence(this.currentTask.subtaskGuid);
        this.toEmails = this.correspondence ? (this.correspondence.correspondence.length > 0 ? CloneHelper.cloneObject(this.correspondence.correspondence[0].emails) : []) : [];
        this.currentTask.isReviewed = true;
        this.onPropertyChanged<Correspondence>(z => z.allItems);
        await this.subtasksService.setAsReviewed(this.currentTask.subtaskId);
        this.subscribeSignalR();
    }

    closeCorrespondence() {
        this.currentTask = null;
        this.title = null;
        this.correspondence = null;
        this.toEmails = [];
    }

    toggle(element: HTMLElement, arrow: HTMLElement) {
        $(element).slideToggle();
        if ($(arrow).hasClass('is-opened')) {
            $(arrow).removeClass('is-opened');
        } else {
            $(arrow).addClass('is-opened');
        }
    }

    addAttachmentToDocuments(x: EntityAttachmentModel) {
        return new Promise<void>((resolve, reject) => {
            this.subtasksService.addAttachmentToEntityDocuments(x.path, x.name, this.correspondenceEntity.entityId, this.correspondenceEntity.entityTypeId, x.attachmentTypeId)
                .then(() => {
                    this.notificationService.show("Add attachment", "Successfully added", NotificationType.Success);

                    if (this.newDocumentAdded) {
                        this.newDocumentAdded();
                    }

                    if (this.rfiChanged) {
                        this.rfiChanged();
                    }

                    this.bindingSignaler.signal('update-title');

                    resolve();
                }, reject);
        });
    }


    public openAttachmentsDialog = () => {
        const isAssignedToMe = this.assignment && this.assignment.isAssignedToMe();
        if (this.isVessel || isAssignedToMe) {
            this.attachmentDialogBroker.rootItem = {
                entityType: EntityTypes.SubtaskCorrespondence,
                identifier: Guid.generateGuid()
            };
            this.attachmentDialogBroker.rootItem["id"] = this.subtaskCorrespondenceId;
            this.attachmentDialogBroker.saveFunc = async () => {
                const subtaskCorrespondenceReponse = await this.subtasksService.reply(this.correspondence.guid, this.subtaskCorrespondenceId, this.correspondenceMessage,
                    false, false, false, [], this.toEmails, this.statusSnippet, this.comment, null);

                if (subtaskCorrespondenceReponse && subtaskCorrespondenceReponse.validationResult && subtaskCorrespondenceReponse.validationResult.errors) {
                    this.notificationService.show(subtaskCorrespondenceReponse.validationResult.errors[0].message, "", NotificationType.Error)
                    return;
                }
                this.subtaskCorrespondenceId = subtaskCorrespondenceReponse.subtaskCorrespondenceId
                this.attachmentDialogBroker.rootItem["id"] = this.subtaskCorrespondenceId;
                return () => {
                };
            };
            this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipAttachmentsDialog, this.attachmentDialogBroker, true, false, false, 'right');
        } else {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: this.getDialogName(),
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                callback: () => this.openAttachmentsDialog(),
                currentAssignment: this.assignment && this.assignment.assignedTo ? this.assignment.assignedToUserName : ''
            }, true, false, false,);
        }
    }

    getDialogName() {
        return this.name ? this.name : this.currentTask.type;
    }

    onSubtaskCorrespondenceReply(x: CorrespondenceClientModel) {
        const exists = this.correspondence.correspondence.filter(a => x.user == a.user && x.message == a.message).length > 0;
        if (!exists) {
            x.createdDate = moment.utc(x.createdDate).toDate();
            this.correspondence.correspondence.splice(0, 0, x);
        }
    }

    @computedFrom("correspondenceEntity", "correspondenceEntity.vetTasks", "correspondenceEntity.vetTasks.length")
    get vetTasks() {
        if (this.correspondenceEntity && this.correspondenceEntity.vetTasks) {
            return this.correspondenceEntity.vetTasks
                .filter(x => Enumerable.from(this.allItems).selectMany(x => x.subtaskLinkedEntities).any(a => a.linkedEntityId == x.vetTaskId && a.linkedEntityTypeId == EntityTypes.VetTask))
                .map(x => {
                    return {
                        name: x.title,
                        id: x.vetTaskId
                    }
                });
        }
        return null;
    }

    filterItems() {
        let query = Enumerable.from(this.allItems);

        if (this.isInternalSelected)
            query = query.where(x => x.isInternal);

        if (this.dateFrom)
            query = query.where(x => new Date(x.date) >= this.dateFrom);
        if (this.dateTo)
            query = query.where(x => new Date(x.date) <= this.dateTo);
        if (this.selectedEntityTypeIds && this.selectedEntityTypeIds.length > 0)
            query = query.where(x => this.selectedEntityTypeIds.indexOf(x.entityTypeId) > -1);
        if (this.selectedSubtaskStatusIds && this.selectedSubtaskStatusIds.length > 0)
            query = query.where(x => this.selectedSubtaskStatusIds.indexOf(x.subtaskStatusId) > -1);
        if (this.selectedVetTaskIds && this.selectedVetTaskIds.length > 0)
            query = query.where(x => x.subtaskLinkedEntities.filter(a => a.linkedEntityTypeId == EntityTypes.VetTask && this.selectedVetTaskIds.indexOf(a.linkedEntityId) > -1).length > 0);

        if (this.correspondence && !query.any(x => x.subtaskGuid == this.correspondence.guid)) //if current selected correspondence is not in filtered list, then unselect correspondence
        {
            this.correspondence = null;
        }

        this.items = query.groupBy(x => x.typeDisplayText).select(x => {
            return {
                key: x.key(),
                value: x.groupBy(x => x.parentSubtaskId ? x.parentSubtaskId : x.subtaskId).select(x => {
                    return {
                        key: x.key(),
                        value: x.orderByDescending(x => x.date).toArray()
                    };
                }).toArray()
            };
        }).toArray();
    }

    subtaskCorrespondenceId: number;

    get isVessel() {
        return this.correspondenceEntity.entityTypeId == EntityTypes.Vessel;
    }

    getWho(c: CorrespondenceClientModel) {
        if (c.user)
            return c.user;

        if (c.emails.length > 0) {
            const from = c.emails.filter(x => x.isFrom)[0];
            if (from) return from.email;
            return c.emails[0].email;
        }
        return null;
    }

    getWhome(c: CorrespondenceClientModel) {
        let str = "";
        if (c.emails.length > 0) {
            for (const t of c.emails) {
                if (!str)
                    str += `To: ${t.email}`;
                else
                    str += `<br/>To: ${t.email}`;
            }
        }
        return str;
    }

    public submitCorrespondence = async () => {

        const validator = await this.validator.validate();
        if (!validator.valid)
            return;

        const subtaskCorrespondenceReponse = await this.subtasksService.reply(this.correspondence.guid, this.subtaskCorrespondenceId, this.correspondenceMessage,
            true, true, this.changeStatusToAwaitingResponse, this.subtaskTypes, this.toEmails, this.statusSnippet, this.comment, null);

        if (subtaskCorrespondenceReponse && subtaskCorrespondenceReponse.validationResult && subtaskCorrespondenceReponse.validationResult.errors) {
            this.notificationService.show(subtaskCorrespondenceReponse.validationResult.errors[0].message, "", NotificationType.Error)
            return;
        }

        this.correspondenceMessage = "";
        this.statusSnippet = "";
        this.comment = "";
        this.changeStatusToAwaitingResponse = true;
        this.subtaskCorrespondenceId = null;
        this.attachmentDialogBroker.items = [];
        if (this.attachmentDialogBroker.rootItem)
            this.attachmentDialogBroker.rootItem["id"] = null;

        this.hideReply = false;
        this.isReplyShowing = false;
    }

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z.correspondenceEntity.entityTypeId))
    get addToDocumentsTitle() {
        return this.correspondenceEntity.entityTypeId == EntityTypes.VesselApplicationForms ? "Add to Form" : "Add to Documents";
    }

    refreshSubtaskStatus(param) {
        if (!this.allItems) return;

        const subtask = this.allItems.filter(x => x.subtaskId == param.subtaskId)[0];
        if (this.correspondenceEntity && subtask) {
            subtask.isReviewed = param.status != SubtaskStatusEnum.PendingReview;
            this.onPropertyChanged<Correspondence>(z => z.allItems);
            const subtaskStatus = this.subtaskStatuses.filter(x => x.id == param.status)[0];
            if (this.correspondence && subtaskStatus && this.correspondence.subtaskStatusId != subtaskStatus.id) {
                this.correspondence.subtaskStatusId = subtaskStatus.id;
                this.correspondence.subtaskStatusHistory.splice(0, 0, {
                    statusId: subtaskStatus.id,
                    createdDate: new Date(),
                    status: subtaskStatus.name,
                    user: param.user ? param.user : "",
                    comment: param.comment
                });
            }
        }
    }

    lastSelectedItemIds: number[] = [];
    subtaskTypes: number[] = [];
    selectedRfiTypes: MultiSelectLookupControl;

    selectedItemsChanged() {
        const notSelected = this.selectedRfiTypes.itemSource.filter((x: any) => this.subtaskTypes.indexOf(x.id) == -1);

        if (this.correspondenceMessage) {
            notSelected.forEach(x => {
                if (!x.additionalData && x.additionalData.template) return;

                const index = this.lastSelectedItemIds.indexOf(x.id);
                if (index >= 0)
                    this.lastSelectedItemIds.splice(index, 1);
                this.correspondenceMessage = this.correspondenceMessage.split(x.additionalData.template).join("");
            });
        }

        if (this.statusSnippet) {
            notSelected.forEach(x => {
                const index = this.lastSelectedItemIds.indexOf(x.id);
                if (index >= 0)
                    this.lastSelectedItemIds.splice(index, 1);
                this.statusSnippet = this.statusSnippet.split(`, ${x.name}`).join("");
                this.statusSnippet = this.statusSnippet.split(`${x.name}, `).join("");
                this.statusSnippet = this.statusSnippet.split(x.name).join("");
            });
        }

        const newItems = this.selectedRfiTypes.itemSource.filter((x: any) => this.subtaskTypes.indexOf(x.id) != -1 && this.lastSelectedItemIds.indexOf(x.id) == -1);
        newItems.forEach(x => {
            if (this.correspondenceMessage && !this.correspondenceMessage.endsWith("<br></p>"))
                this.correspondenceMessage += "<br/>";
            else if (!this.correspondenceMessage) {
                this.correspondenceMessage = "";
            }

            if (this.statusSnippet && !this.statusSnippet.endsWith(", "))
                this.statusSnippet += ", ";
            else if (!this.statusSnippet) {
                this.statusSnippet = "";
            }

            if (x && x.additionalData && x.additionalData.template)
                this.correspondenceMessage += x.additionalData.template;
            if (x && x.name)
                this.statusSnippet += x.name;

            this.lastSelectedItemIds.push(x.id);
        });
    }

    @computedFrom(Reflection.getPropertyPath<Correspondence>(z => z.allItems),
        Reflection.getPropertyPath<Correspondence>(z => z.changes.allItems),
        Reflection.getPropertyPath<Correspondence>(z => z.allItems.length))
    get isReviewed() {
        if (this.allItems) {
            return !(this.allItems.filter(x => !x.isReviewed).length > 0);
        }
        return true;
    }

    toggleReply() {
        const isAssignedToMe = this.assignment && this.assignment.isAssignedToMe();
        if (!this.assignment || isAssignedToMe) {
            this.isReplyShowing = !this.isReplyShowing;
            if (this.currentTask && this.currentTask.isInvitation) {
                this.lastSelectedItemIds = this.currentTask.subtaskTypeIds;
                this.subtaskTypes = this.currentTask.subtaskTypeIds;
                this.correspondenceMessage = this.correspondence && this.correspondence.correspondence && this.correspondence.correspondence.length > 0 ?
                    this.correspondence.correspondence[0].message : null;
            }
            this.setRFIRules();
        } else {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: this.getDialogName(),
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                        this.toggleReply();
                    }
                },
                currentAssignment: this.assignment && this.assignment.assignedTo ? this.assignment.assignedToUserName : ''
            }, true, false, false);
        }
    }

    getTaskName(task: CorrespondenceItemModel): string {
        if (!task) return "";
        const name = !task.name && (task.entityTypeId == EntityTypes.Vet || task.entityTypeId == EntityTypes.FBR || task.entityTypeId == EntityTypes.VoyageDataSubmission) ? "Requested Information" : task.name;
        return task.isInvitation ? `${name}  (INV-${task.subtaskId})` : task.isInternal ? `${name} (RFI-${task.subtaskId}) - <span class="internal">Internal RFI</span>` : `${name} (RFI-${task.subtaskId})`;
    }

    isInternalSelected: boolean = false;
    toggleIsInternal() {
        this.isInternalSelected = !this.isInternalSelected;
        this.filterItems();
    }

    @computedFrom("correspondenceEntity", "correspondenceEntity.portCountryOfLoadSanctioned", "correspondenceEntity.portCountryOfDischargeSanctioned")
    get isSanctionedCountry(): boolean {
        return this.correspondenceEntity.portCountryOfLoadSanctioned || this.correspondenceEntity.portCountryOfDischargeSanctioned;
    }

    @computedFrom("correspondenceEntity", "correspondenceEntity.portCountryOfDischargeUnknown", "correspondenceEntity.portCountryOfLoadUnknown")
    get isUnknownCountry(): boolean {
        return this.correspondenceEntity.portCountryOfDischargeUnknown || this.correspondenceEntity.portCountryOfLoadUnknown;
    }

    @computedFrom("isSanctionedCountry", "canProcessSanctionedVet", "isUnknownCountry", "canProcessVetUnknownPort")
    get canProcessVet() {
        return (!this.isSanctionedCountry || this.canProcessSanctionedVet) &&
            (!this.isUnknownCountry || this.canProcessVetUnknownPort)
    }

    @observable()
    public correspondenceFilter: string;

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;
        this.correspondence = null;

        if (e.key == "Enter") {
            this.loadSubtasks();
        }
    }

    correspondenceFilterChanged() {
        if ((this.correspondenceFilter && this.correspondenceFilter.length >= 3) || this.correspondenceFilter == "") {
            this.loadSubtasks();
        }
    }

    clearFilter() {
        this.correspondenceFilter = null;
        this.loadSubtasks();
    }

    initializeFilters() {
        const promises = [
            this.lookupClientService.getLookup(new LookupValueRequest(LookupValueQuery.SubtaskTypes)),
            this.lookupClientService.getLookup(new LookupValueRequest(LookupValueQuery.SubtaskStatuses))
        ]
        Promise.all(promises).then(x => {
            this.allSubtaskTypes = x[0].activeValues;
            this.subtaskStatuses = x[1].activeValues;
        });
    }

    closeAllDropdowns() {
        this.showEntityTypeFilter = false;
        this.showRFITypeFilter = false;
        this.showSubtaskStatusFilter = false;
        this.showVetTasksFilter = false;
    }

    //#region entity type filter
    @observable()
    public showEntityTypeFilter: boolean = false;
    public selectedEntityTypeIds: number[] = [];

    @computedFrom("map", "map.length", "correspondenceEntity.entityTypeId")
    public get entityTypes() {
        const availableEntityTypes = Enumerable.from(this.map).where(x => x.entityType == this.correspondenceEntity.entityTypeId).selectMany(x => x && x.dependencies && x.dependencies.length > 0 ? x.dependencies : [x.entityType])
            .select(x => {
                return { id: x, name: EntityTypes[x] };
            }).toArray();
        return availableEntityTypes;
    }

    public toggleEntityTypeFilter($event) {
        if ($event)
            $event.stopPropagation();
        if (!this.showEntityTypeFilter) this.closeAllDropdowns();
        this.showEntityTypeFilter = !this.showEntityTypeFilter;
    }

    showEntityTypeFilterChanged() {
        if (this.showEntityTypeFilter == false) {
            this.filterItems();
        }
    }

    @computedFrom("selectedEntityTypeIds", "selectedEntityTypeIds.length")
    public get entityTypeFilterActive(): boolean {
        return this.selectedEntityTypeIds && this.selectedEntityTypeIds.length > 0;
    }

    public clearEntityTypeFilter() {
        this.selectedEntityTypeIds = [];
        this.filterItems();
    }

    //#endregion

    //#region rfi type filter
    @observable()
    public showRFITypeFilter: boolean = false;
    public selectedRFITypeIds: number[] = [];
    public allSubtaskTypes: LookupValue[] = [];

    @computedFrom("selectedEntityTypeIds", "selectedEntityTypeIds.length", "allSubtaskTypes", "allSubtaskTypes.length", "entityTypes", "entityTypes.length")
    public get rfiTypes() {
        if (this.allSubtaskTypes && this.allSubtaskTypes.length > 0) {
            const rfiTypeIds = Enumerable.from(this.allItems).selectMany(x => x.subtaskTypeIds);
            const allSubtaskTypes = this.allSubtaskTypes.filter(x => rfiTypeIds.contains(x.id));
            if (this.selectedEntityTypeIds && this.selectedEntityTypeIds.length > 0) {
                const categories = this.map.filter(x => this.selectedEntityTypeIds.indexOf(x.entityType) > -1).map(x => x.rfiTemplateCategory);
                return allSubtaskTypes.filter(x => categories.indexOf(x.additionalData["rfiTemplateCategoryId"]) > -1)
                    .map(x => {
                        return {
                            id: x.id,
                            name: x.name + (this.selectedEntityTypeIds.length > 1 ? " (" + RfiTemplateCategoryEnum[x.additionalData["rfiTemplateCategoryId"]] + ")" : "")
                        };
                    });
            } else if (this.entityTypes && this.entityTypes.length) {
                const categories = this.map.filter(x => this.entityTypes.findIndex(e => e.id == x.entityType) > -1).map(x => x.rfiTemplateCategory);
                return allSubtaskTypes.filter(x => categories.indexOf(x.additionalData["rfiTemplateCategoryId"]) > -1)
                    .map(x => {
                        return {
                            id: x.id,
                            name: x.name + (this.entityTypes.length > 1 ? " (" + RfiTemplateCategoryEnum[x.additionalData["rfiTemplateCategoryId"]] + ")" : "")
                        };
                    });
            } else return allSubtaskTypes.map(x => {
                return {
                    id: x.id,
                    name: x.name + " (" + RfiTemplateCategoryEnum[x.additionalData["rfiTemplateCategoryId"]] + ")"
                }
            });
        }
        return [];
    }

    public toggleRFITypeFilter($event) {
        if ($event)
            $event.stopPropagation();
        if (!this.showRFITypeFilter) this.closeAllDropdowns();
        this.showRFITypeFilter = !this.showRFITypeFilter;
    }

    showRFITypeFilterChanged() {
        if (this.showRFITypeFilter == false) {
            this.loadSubtasks();
        }
    }

    @computedFrom("selectedRFITypeIds", "selectedRFITypeIds.length")
    public get rfiTypeFilterActive(): boolean {
        return this.selectedRFITypeIds && this.selectedRFITypeIds.length > 0;
    }

    public clearRFITypeFilter() {
        this.selectedRFITypeIds = [];
        this.loadSubtasks();
    }

    //#endregion

    //#region subtask status filter
    @observable()
    public showSubtaskStatusFilter: boolean = false;
    //#region subtask status filter
    @observable()
    public showVetTasksFilter: boolean = false;


    public selectedSubtaskStatusIds: number[] = [];
    public selectedVetTaskIds: number[] = [];

    public toggleSubtaskStatusFilter($event) {
        if ($event)
            $event.stopPropagation();
        if (!this.showSubtaskStatusFilter) this.closeAllDropdowns();
        this.showSubtaskStatusFilter = !this.showSubtaskStatusFilter;
    }

    public toggleVetTasksFilter($event) {
        if ($event)
            $event.stopPropagation();
        if (!this.showVetTasksFilter) this.closeAllDropdowns();
        this.showVetTasksFilter = !this.showVetTasksFilter;
    }

    showSubtaskStatusFilterChanged() {
        if (this.showSubtaskStatusFilter == false) {
            this.filterItems();
        }
    }

    showVetTasksFilterChanged() {
        if (this.showVetTasksFilter == false) {
            this.filterItems();
        }
    }

    @computedFrom("selectedSubtaskStatusIds", "selectedSubtaskStatusIds.length")
    public get subtaskStatusFilterActive(): boolean {
        return this.selectedSubtaskStatusIds && this.selectedSubtaskStatusIds.length > 0;
    }

    @computedFrom("selectedVetTaskIds", "selectedVetTaskIds.length")
    public get vetTasksFilterActive(): boolean {
        return this.selectedVetTaskIds && this.selectedVetTaskIds.length > 0;
    }

    public clearSubtaskStatusFilter() {
        this.selectedSubtaskStatusIds = [];
        this.filterItems();
    }

    public clearVetTasksFilter() {
        this.selectedVetTaskIds = [];
        this.filterItems();
    }

    //#endregion

    @computedFrom("correspondenceFilter", "selectedRFITypeIds", "selectedRFITypeIds.length")
    get hasfilerApplied() {
        return (this.correspondenceFilter != null && this.correspondenceFilter.trim() != "")
            || (this.rfiTypeFilterActive || (!this.rfiTypeFilterActive && this.showRFITypeFilter))
    }

    filterDropdownClick($event) {
        if ($event)
            $event.stopPropagation();
    }

    removeEmail(i) {
        this.toEmails.splice(i, 1);
    }

    addEmail() {
        this.toEmails.push({ email: "", emailRecipientTypeId: null });
    }

    public remove($event, item: RightshipEntityAttachmentModel) {
        $event.stopPropagation();

        const model: RightshipConfirmationDialogModel = {
            title: "Delete Document",
            messages: ["Are you sure you want to delete this document?"],
            cancel: "Cancel",
            confirm: "Delete",
            messageClasses: [],
            onConfirm: () => {
                this.removeInternal($event, item).then(() => {
                    this.notificationService.show(this.i18N.tr("attachment-was-deleted-text"), "", NotificationType.Success);
                    this.dialogService.hide();
                }).catch(() => {
                    this.notificationService.show(this.i18N.tr("error-text") + "!", this.i18N.tr("attachment-could-not-be-deleted-text"), NotificationType.Error);
                    this.dialogService.hide();
                });
            }
        };

        this.dialogService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
    }

    protected removeInternal($event, item: RightshipEntityAttachmentModel) {
        $event.stopPropagation();
        return this.attachmentClientService.deleteCorrespondenceAttachments({
            entityAttachmentIds: [item.entityAttachmentId],
            correspondenceGuid: null
        }).then(() => {

            if (!this.attachmentDialogBroker.items) {
                this.attachmentDialogBroker.items = [];
            }
            const index = this.attachmentDialogBroker.items.indexOf(item);
            if (index > -1) {
                this.attachmentDialogBroker.items.splice(index, 1);
            }

            if (this.attachmentDialogBroker.onAttachmentsModified) {
                this.attachmentDialogBroker.onAttachmentsModified([]);
            }
        });
    }
}
